import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/i18n/useLocale";
import { Mail, ShieldCheck, FileText, ArrowUpRight } from "lucide-react";

const Privacy = () => {
  const { t } = useTranslation();
  const { L } = useLocale();

  return (
    <div data-testid="page-privacy" className="pt-[72px] bg-brand-cream min-h-screen">
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-25" />
        <div className="container-wide py-16 md:py-20 relative">
          <p className="eyebrow-light mb-4">◦  Privacy & Data Handling</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            Privacy policy <span className="italic text-brand-gold">— in plain English.</span>
          </h1>
          <p className="mt-5 text-brand-cream/75 max-w-2xl leading-relaxed text-sm md:text-base">
            We are a B2B pre-launch platform. We collect a minimum amount of business data, use it only for technical follow-up, and never sell or share leads.
          </p>
        </div>
      </section>

      <div className="container-wide py-14 md:py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <aside className="lg:col-span-1">
          <div className="bg-white border border-brand-green/15 p-6">
            <p className="eyebrow mb-3">◦  At a glance</p>
            <ul className="space-y-3 text-sm text-brand-black/75">
              <li className="flex gap-3"><ShieldCheck size={16} className="text-brand-teal shrink-0 mt-0.5" /> Lawful basis: explicit opt-in consent (GDPR Art. 6(1)(a))</li>
              <li className="flex gap-3"><FileText size={16} className="text-brand-teal shrink-0 mt-0.5" /> Data minimisation: only business email, name, mobile, company &amp; intent</li>
              <li className="flex gap-3"><Mail size={16} className="text-brand-teal shrink-0 mt-0.5" /> Contact: <a href="mailto:privacy@spiruva.com" className="underline hover:text-brand-teal">privacy@spiruva.com</a></li>
            </ul>
          </div>

          <Link
            to={L("/resources")}
            data-testid="privacy-back-resources"
            className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-brand-black/65 hover:text-brand-gold transition"
          >
            Back to Resources <ArrowUpRight size={12} />
          </Link>
        </aside>

        <article className="lg:col-span-2 space-y-8 text-brand-black/85 leading-relaxed">
          <Section title="1. Who we are" testid="privacy-section-controller">
            SPIRUVA is operated by Carbofixers, an Indian-registered entity. For any data-protection question, write to{" "}
            <a href="mailto:privacy@spiruva.com" className="underline hover:text-brand-teal">privacy@spiruva.com</a>.
          </Section>

          <Section title="2. What we collect" testid="privacy-section-collect">
            When you download a brochure, use case, or fact sheet, we collect: your full name, business email, mobile number (with country code), company name, intent of use, and the asset you requested. We also log the timestamp of your consent and a hashed approximation of your IP address. We do <strong>not</strong> collect payment data on this site.
          </Section>

          <Section title="3. Why we collect it (lawful basis)" testid="privacy-section-basis">
            Your data is processed on the basis of <strong>explicit opt-in consent</strong> (GDPR Art. 6(1)(a)) provided via the download form. We use it solely to:
            <ul className="list-disc pl-5 mt-3 space-y-1">
              <li>Send the requested document and follow-up technical material;</li>
              <li>Plan our 2027 supply allocation;</li>
              <li>Respond to procurement, R&amp;D and distribution enquiries.</li>
            </ul>
          </Section>

          <Section title="4. Retention" testid="privacy-section-retention">
            Lead records are retained for up to 36 months from your last interaction, after which we anonymise or delete them. You may request earlier deletion at any time.
          </Section>

          <Section title="5. Your rights (GDPR / DPDP)" testid="privacy-section-rights">
            You have the right to access, correct, delete, port or object to processing of your personal data. To exercise any of these rights, email{" "}
            <a href="mailto:privacy@spiruva.com" className="underline hover:text-brand-teal">privacy@spiruva.com</a>. EU residents may also complain to their local supervisory authority.
          </Section>

          <Section title="6. Withdrawing consent" testid="privacy-section-withdraw">
            You can withdraw your consent at any time by replying "unsubscribe" to any email we send, or by emailing{" "}
            <a href="mailto:privacy@spiruva.com" className="underline hover:text-brand-teal">privacy@spiruva.com</a>. Withdrawing consent does not affect the lawfulness of processing carried out before withdrawal.
          </Section>

          <Section title="7. Sharing" testid="privacy-section-sharing">
            We do not sell or share lead data with third parties. Operational sub-processors are limited to: transactional email (Resend), hosting (Emergent / AWS) and analytics. All sub-processors are contractually bound to the same data-protection standards.
          </Section>

          <Section title="8. Updates" testid="privacy-section-updates">
            This policy may be updated as our platform evolves. The latest version is always published at{" "}
            <Link to={L("/privacy")} className="underline hover:text-brand-teal">/privacy</Link>.
          </Section>
        </article>
      </div>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <section data-testid={testid}>
    <h2 className="font-display text-2xl text-brand-black mb-3 tracking-tight">{title}</h2>
    <div className="text-sm md:text-[15px]">{children}</div>
  </section>
);

export default Privacy;
